import { useCallback, useMemo, MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tag, TagCloseButton, TagLabel, CloseIcon } from '@cdl/heartbeat-react';
import { manualAbnormalitiesSelector } from '../selectors/user-diagnostic.selectors';
import { saveManualAbnormalities } from '../thunks/save-manual-abnormalities.thunk';
import {
  Abnormality,
  Frequency,
  Likelihood,
  Localization,
  Modifier,
  Temporality,
} from '../types/diagnostic';
import { needToAssessSelector } from '../../autoLabels/selectors/auto-labels.selectors';

interface Props {
  abnormality: Abnormality;
  modifier: Modifier;
  modifierValue: Frequency | Temporality | Likelihood | Localization;
}

export const DiagnosticModifier: React.FC<Props> = ({
  abnormality,
  modifier,
  modifierValue,
}) => {
  const dispatch = useDispatch();
  const isFocusDisabled = useSelector(needToAssessSelector);
  const tabIndex = isFocusDisabled ? -1 : 0;
  const storedAbnormalities = useSelector(manualAbnormalitiesSelector);
  const abnormalitiesByCode = useMemo(
    () =>
      storedAbnormalities.reduce(
        (acc, storedAbnormality) => ({
          ...acc,
          [storedAbnormality.code]: storedAbnormality,
        }),
        {} as Record<string, Abnormality>,
      ),
    [storedAbnormalities],
  );

  const deleteModifier = useCallback(
    (
      modifier: Modifier,
      event: MouseEvent<SVGElement, globalThis.MouseEvent>,
    ) => {
      event.stopPropagation();
      const newAbnormality = {
        ...abnormalitiesByCode[abnormality.code],
        [modifier]: null,
      };
      const updatedAbnormalities = {
        ...abnormalitiesByCode,
        [newAbnormality.code]: newAbnormality,
      };

      dispatch(saveManualAbnormalities(Object.values(updatedAbnormalities)));
    },
    [dispatch, abnormalitiesByCode, abnormality],
  );

  return (
    <Tag colorScheme='blue' data-testid={`${modifier}-modifier`} size='sm'>
      <TagLabel>{modifierValue}</TagLabel>
      <TagCloseButton tabIndex={tabIndex}>
        <CloseIcon
          onClick={(event) => deleteModifier(modifier, event)}
          data-testid='clear-modifier'
        />
      </TagCloseButton>
    </Tag>
  );
};
